import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router'
import { MegaOrganisationService, MegaSessionService } from '@megaphone/services'
import { Organisation } from '@megaphone/types'
import { OrganisationService } from '@services/organisation.service'

@Injectable({
  providedIn: 'root',
})
export class OrganizationGuard {
  constructor(
    private _router: Router,
    private _organisationService: OrganisationService,
    private _megaSessionService: MegaSessionService,
    private _megaOrganisationService: MegaOrganisationService,
  ) {}

  async canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) {
    const url = _state.url
    const urlTree = this._router.parseUrl(url)
    const organizationId = _route.params.organizationId
    const urlWithoutParams = urlTree.root.children['primary']?.segments
      .map((it) => it.path)
      .join('/')

    if (!(await this._checkUserToken(_route.queryParams?.token, urlWithoutParams, organizationId)))
      return false
    if (!this._unknownUserGuard(url)) return false
    if (!this._userGuard()) return false

    if (!isNaN(Number(organizationId)) && localStorage.getItem('token')) {
      if (this._megaOrganisationService.attributes?.id == organizationId) return true

      const targetedOrganization = await this._getTargetedOrganisation(organizationId)

      if (!targetedOrganization) {
        console.warn('Targeted organization does not exist')
        await this._applyOrganisationId(url)
        return true
      } else if (targetedOrganization?.id !== this._megaOrganisationService.attributes?.id) {
        await this._setOrganisation(targetedOrganization, url)
        return false
      }
    }

    await this._applyOrganisationId(url)
    return false
  }

  private async _getTargetedOrganisation(organizationId: number): Promise<Organisation> {
    return new Promise((resolve) => {
      this._organisationService.getAllItems().then((items: Organisation[]) => {
        return resolve(
          items?.find((organization: Organisation) => organization.id == organizationId),
        )
      })
    })
  }

  private async _setOrganisation(organisation: Organisation, url: string) {
    await this._organisationService.setOrganisation(organisation)
    this._reloadCurrentRoute(url)
  }

  private _reloadCurrentRoute(url: string) {
    const targetUrl = url.startsWith('/') ? url : `/${url}`
    window.location.href = window.location.origin + targetUrl
  }

  private async _applyOrganisationId(url: string) {
    if (!this._megaOrganisationService.attributes?.id)
      return this._router.navigate(['/auth/login'], {
        queryParams: url.includes('dashboards') // Avoid redirect on dashboard
          ? {}
          : { returnUrl: url },
      })

    const newUrl = `${this._megaOrganisationService.attributes.id}/${url}`

    await this._router.navigate([newUrl], {
      queryParamsHandling: 'replace',
    })
  }

  private async _checkUserToken(token: string, route: string, organizationId: number) {
    if (token) {
      this._megaSessionService.setToken(token)
      await this._megaSessionService.initSession()

      await this._megaOrganisationService.init()

      if (!(await this._getTargetedOrganisation(organizationId))) {
        await this._applyOrganisationId(route)

        return false
      }
    }
    return true
  }

  private async _unknownUserGuard(url: string) {
    if (localStorage.getItem('token') && this._megaSessionService.session?.logged) {
      await this._megaOrganisationService.init()
      // @ToDo admin specific logic
      return true
    }

    // User isn't logged, redirect to the login page
    localStorage.removeItem('token')
    this._router.navigate(['/auth/login'], {
      queryParams: { returnUrl: url },
    })

    return false
  }

  private _userGuard() {
    const userRoles = this._megaSessionService.attributes$.getValue()?.roles
    if (userRoles?.length <= 1 && userRoles?.find((role) => role.role === 'user')) {
      this._router.navigate(['/lost'])
      return false
    }
    return true
  }
}
